import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faEye, faEyeSlash, faSpinner, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../context/AuthContext';
import { getAndClearRedirectPath } from '../utils/authUtils';
import Turnstile from 'react-turnstile';
import logo from '../assets/logo.png';
import gokuhome from '../assets/gokuhome.jpg';
import gokuhomemobile from '../assets/gokuhomemobile.jpg';

// Auth Container
const AuthContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-image: url(${gokuhome});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  @media (max-width: 768px) {
    background-image: url(${gokuhomemobile});
    padding: 1rem;
  }
`;

// Auth Card
const AuthCard = styled.div`
  background-color: var(--background-card);
  border-radius: 20px;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  transition: all 0.2s ease;
  animation: fadeIn 0.5s ease;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 768px) {
    max-width: 98%;
    width: 98%;
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

// Auth Header
const AuthHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

// Auth Logo
const AuthLogo = styled(Link)`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  text-decoration: none;
`;

// Logo Image
const LogoImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
`;

// Auth Title
const AuthTitle = styled.h1`
  margin: 0 0 1rem;
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--text-color);
`;

// Auth Subtitle
const AuthSubtitle = styled.p`
  color: var(--text-dim);
  font-size: 0.9rem;
  margin: 0;
`;

// Auth Form
const AuthForm = styled.form`
  margin-bottom: 1.5rem;
`;

// Form Group
const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

// Form Label
const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 400;
  color: var(--text-color);
`;

// Input Icon Wrapper
const InputIconWrapper = styled.div`
  position: relative;
`;

// Input Icon
const InputIcon = styled(FontAwesomeIcon)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-dim);
`;

// Input Field
const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: none;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--text-color);
  font-size: 1rem;
  box-sizing: border-box;
  
  &::placeholder {
    color: var(--text-dim);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

// Password Toggle
const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-dim);
  cursor: pointer;
`;

// Auth Message
const AuthMessage = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  text-align: center;
  display: ${props => props.visible ? 'flex' : 'none'};
  background-color: ${props => props.type === 'error' ? 'rgba(239, 71, 111, 0.2)' : 
    props.type === 'success' ? 'rgba(44, 182, 125, 0.2)' : 'rgba(52, 152, 219, 0.1)'};
  color: ${props => props.type === 'error' ? 'var(--error-color)' : 
    props.type === 'success' ? 'var(--success-color)' : 'var(--info-color)'};
  align-items: center;
  justify-content: center;
  
  svg {
    margin-right: 8px;
  }
`;

// Button
const Button = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-size: 1rem;
  text-align: center;
  margin-bottom: 1rem;
  background-color: var(--primary-color);
  color: white;
  
  &:hover {
    background-color: var(--primary-light);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

// Auth Footer
const AuthFooter = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

// Auth Footer Text
const AuthFooterText = styled.p`
  margin: 0;
  color: var(--text-dim);
  font-size: 0.9rem;
  
  a {
    color: var(--primary-light);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

// Turnstile Container
const TurnstileContainer = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
`;

const Login = () => {
  const { login, loading, error: authError, loginWithTurnstile, currentUser } = useContext(AuthContext);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);
  
  // If user is already logged in, redirect to saved path or home
  useEffect(() => {
    if (currentUser && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      
      const redirectPath = getAndClearRedirectPath();
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    }
  }, [currentUser, navigate]);
  
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!username || !password) {
      setError('Please fill in all fields');
      return;
    }
    
    if (!turnstileToken) {
      setError('Please complete the captcha verification');
      return;
    }
    
    try {
      // Use loginWithTurnstile with the dummy token
      await loginWithTurnstile(username, password, turnstileToken);
      // Redirect will happen in the useEffect above
    } catch (err) {
      // Error is already handled in the context and the useEffect above
    }
  };
  
  // If already logged in, don't render the login form
  if (currentUser) {
    return null;
  }
  
  return (
    <AuthContainer>
      <AuthCard>
        <AuthHeader>
          <AuthLogo to="/">
            <LogoImage src={logo} alt="Odelu" />
          </AuthLogo>
          <AuthTitle>Welcome Back</AuthTitle>
          <AuthSubtitle>Sign in to your account to continue</AuthSubtitle>
        </AuthHeader>
        
        <AuthForm onSubmit={handleSubmit}>
          <FormGroup>
            <FormLabel htmlFor="username">Username</FormLabel>
            <InputIconWrapper>
              <InputIcon icon={faUser} />
              <Input 
                type="text" 
                id="username" 
                name="username" 
                placeholder="your_username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </InputIconWrapper>
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="password">Password</FormLabel>
            <InputIconWrapper>
              <InputIcon icon={faLock} />
              <Input 
                type={showPassword ? "text" : "password"} 
                id="password" 
                name="password" 
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <PasswordToggle 
                type="button" 
                onClick={() => setShowPassword(!showPassword)}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </PasswordToggle>
            </InputIconWrapper>
          </FormGroup>
          
          {/* Turnstile temporarily disabled for testing */}
          <TurnstileContainer>
            <Turnstile
              sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAABgR8wgDATOEb-vD"}
              onVerify={setTurnstileToken}
              theme="dark"
            />
          </TurnstileContainer>
          
          <Button type="submit" disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Sign In"}
          </Button>
          
          <AuthMessage visible={!!error} type="error">
            <FontAwesomeIcon icon={faExclamationCircle} />
            {error}
          </AuthMessage>
        </AuthForm>
        
        <AuthFooter>
          <AuthFooterText>
            Don't have an account? <Link to="/register">Sign up</Link>
          </AuthFooterText>
        </AuthFooter>
      </AuthCard>
    </AuthContainer>
  );
};

export default Login; 